import React, { useEffect, useRef, useState } from 'react'
import { Alert, AlertIcon, Box, Breadcrumb, BreadcrumbItem, BreadcrumbLink, Button, Flex, Image, Input, Progress, Stack, Text, useColorModeValue, useToast } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CiImport } from "react-icons/ci";

import { useStateContext } from '../../../context/ContextProvider';
import axiosClient from '../../../axios-client';

import img from './assets/images/logo_Excel.png'
import templateFile from './assets/Files/Exemple_Modification.xlsx';

function ModifyOrdersFromExcel() {
    const navigate = useNavigate()
    const { user, setToken, setUser, getAbonnementClient, currentBusiness } = useStateContext()
    const { t } = useTranslation();
    const [file, setFile] = useState(null);
    const toast = useToast()
    const [progress, setProgress] = useState(0);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const [typeFileErrors, setTypeFileErrors] = useState([])

    const handleFileChange = (e) => {
        const file = e.target.files[0]
        if (file && file?.name.length > 70) {
            toast({
                description: t("Le nom du fichier ne doit pas dépasser 60 caractères"),
                variant: 'left-accent',
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right'
            })
            setFile(null)
            if (fileInputRef.current) fileInputRef.current.value = null;
            return;
        }
        else if (file && !['.xlsx', '.xls', '.csv'].includes(file?.name.substring(file?.name.lastIndexOf('.')))) {
            toast({
                description: t("Il faut respecter les extensions du fichier [.xlsx , .xls , .csv]"),
                variant: 'left-accent',
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right',
            })
            setFile(null)
            if (fileInputRef.current) fileInputRef.current.value = null;
            return;
        }
        else setFile(file);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const file = e.dataTransfer.files[0];
        if (file) handleFileChange({ target: { files: [file] } });
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const downloadCSV = () => {
        const csvContent = "data:text/csv;charset=utf-8," +
            "id ordre,confirmation status,delivred status,total price,quantity\n" +
            "OR01,confirmed,dilivered,150.00,2\n" +
            "OR02,concelled,returned,0.00,1\n" +
            "OR03,confirmed,shipped,80.00,1\n";
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "Exemple_Modification.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleSubmit = async () => {
        if (uploading) return;
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        setUploading(true);
        setTypeFileErrors([]);
        try {
            const response = await axiosClient.post(`modify-orders-excel/${user?.id}/?business=${currentBusiness?.id}`, formData, {
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setProgress(percentCompleted);
                }
            });

            getAbonnementClient();

            if (response.data.error === 'Client is disabled.') {
                setUser({});
                setToken(null);
                localStorage.clear();

                if (!toast.isActive('accountDisabled')) {
                    toast({
                        id: 'accountDisabled',
                        description: t("le compte est désactivé"),
                        status: 'warning',
                        variant: 'left-accent',
                        duration: 2000,
                        isClosable: true,
                        position: 'bottom-right'
                    });
                }
                return;
            }

            if (response.data.message === "Modification terminée avec des erreurs") {
                setTypeFileErrors(response.data.errors);
            }

            toast({
                description: t(`Fichier importé. ${response.data.orders_modified || 0} commande(s) modifiée(s).`),
                variant: 'left-accent',
                status: response.data.errors ? 'warning' : 'success',
                duration: 4000,
                isClosable: true,
                position: 'bottom-right'
            });

        } catch (error) {
            let errorMessage = t("Une erreur s'est produite lors de l'importation du fichier.");
            if (error.response) {
                const status = error.response.status;
                const serverMessage = error.response.data?.message || error.response.data?.error;

                if (serverMessage === 'Client is disabled.') {
                    setUser({});
                    setToken(null);
                    localStorage.clear();

                    if (!toast.isActive('accountDisabled')) {
                        toast({
                            id: 'accountDisabled',
                            description: t("le compte est désactivé"),
                            status: 'warning',
                            variant: 'left-accent',
                            duration: 2000,
                            isClosable: true,
                            position: 'bottom-right'
                        });
                    }
                    return;
                }

                if (status === 413) errorMessage = t("Le fichier est trop volumineux.");
                else if (status === 415) errorMessage = t("Type de fichier non supporté.");
                else if (status === 401) errorMessage = t("Non autorisé. Veuillez vous reconnecter.");
                else if (status === 403) errorMessage = t("Accès interdit.");
                else if (status >= 500) errorMessage = t("Erreur serveur. Veuillez réessayer plus tard.");
                else if (serverMessage) errorMessage = serverMessage;
            } else if (error.request) {
                errorMessage = t("Problème de connexion. Vérifiez votre connexion internet.");
            }
            toast({
                description: errorMessage,
                variant: 'left-accent',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'bottom-right'
            });
        } finally {
            setUploading(false);
            setProgress(0);
        }
    };

    return (
        <Box position={"relative"} mb={5}>
            <Flex>
                <Box w="100%" display={"flex"} justifyContent="space-between" flexDirection={{ base: 'column', md: "row" }} mt={2} mb={{ base: 0, md: 5 }}>
                    <Breadcrumb  fontStyle={"normal"} fontSize={{ base: "14px", sm: "14px", lg: "15px" }} color="gray.400" letterSpacing="-0.01em">
                        <BreadcrumbItem>
                            <BreadcrumbLink onClick={() => navigate('/')}>{t("Accueil")}</BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbItem isCurrentPage>
                            <BreadcrumbLink>{t("Modification par Excel")} </BreadcrumbLink>
                        </BreadcrumbItem>
                    </Breadcrumb>
                </Box>
            </Flex>

            <Flex
                px={2}
                py={5}
                mx={5}
                bg={useColorModeValue('white', 'gray.700')}
                rounded={"2xl"}
                mt={2}
                align={'center'}
                justify={'center'}
            >
                <Stack
                    w={'full'}
                    maxW='full'
                    rounded={'lg'}
                    mt={2}
                    px={{ base: 1, md: 5 }}
                >
                    <Flex flexDirection='column' mb={4} p={3} bg={useColorModeValue('purple.50', 'gray.600')} rounded="md" borderLeft="4px solid" borderColor="purple.400">
                        <Text fontWeight="semibold" mb={1}>
                            {t("💡 Information importante :")}
                        </Text>
                        <Text fontSize="sm">
                            {t("Vous pouvez importer directement le fichier Excel/CSV fourni par votre société de livraison.")}
                        </Text>
                        <Text fontSize="sm">
                            {t("Alternativement, vous pouvez utiliser notre")}&nbsp;
                            <a
                                href={templateFile}
                                style={{
                                    color: '#299ccb', textDecoration: "underline",
                                    cursor: 'pointer',
                                    fontWeight: '500'
                                }}
                                download="Exemple_Modification.xlsx"
                            >
                                {t("fichier Exemple_Modification.xlsx")}
                            </a>
                            &nbsp;{t("pour remplir vos modifications manuellement.")}
                        </Text>
                        <Text fontSize="sm" mt={2} fontWeight="medium" color={useColorModeValue('purple.700', 'purple.200')}>
                            {t("⚠️ Remarque : La modification de la quantité via Excel modifie uniquement la quantité totale de la commande et n'impacte pas les quantités individuelles des produits associés.")}
                        </Text>
                    </Flex>

                    <Flex position='relative' cursor='pointer' _hover={{ bg: useColorModeValue('gray.200', 'gray.800') }} flexDirection='column' alignItems='center' justifyContent='center' h={150} border={`2px dashed`} borderColor={useColorModeValue('purple.400', 'purple.600')} rounded='lg'
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                    >
                        {file ?
                            <Flex alignItems='center' gap={2}>
                                <Box h={25} w={25}>
                                    <Image src={img} />
                                </Box>
                                <Text>{file?.name}</Text>
                            </Flex>
                            :
                            <>
                                <CiImport size={45} />
                                <Text>
                                    {t('Faites glisser et déposez votre fichier ici')}&nbsp;
                                    [.xlsx , .xls , .csv]
                                </Text>
                            </>
                        }
                        <Input ref={fileInputRef}
                            position="absolute" top={0} left={0} h="100%" opacity={0} type='file' accept=".xlsx, .xls, .csv" onChange={handleFileChange} />
                    </Flex>
                    {uploading && (
                        <Progress
                            hasStripe
                            isAnimated
                            value={progress}
                            size='sm'
                            width='100%'
                            mt={2}
                        />
                    )}
                    <Flex justifyContent='end' gap={2}>
                        {file &&
                            <Button colorScheme='red' onClick={() => {
                                if (fileInputRef.current) fileInputRef.current.value = null;
                                setFile(null)
                            }}>{t("Annuler")}</Button>
                        }
                        <Button isDisabled={!file || uploading} isLoading={uploading} variant='outline' colorScheme='purple' onClick={handleSubmit}>
                            {uploading ? t("Modification en cours...") : t("Terminer la modification")}
                        </Button>
                    </Flex>
                </Stack>
            </Flex>

            {typeFileErrors.length !== 0 && (
                <Box
                    px={2}
                    py={5}
                    mx={5}
                    rounded={"2xl"}
                    mt={2}>
                    {typeFileErrors.map((error, key) => (
                        <Alert key={key} status="error" mb={8} borderRadius="md">
                            <AlertIcon />
                            {t(`${error}`)}
                        </Alert>
                    ))}
                </Box>
            )}
        </Box>
    )
}

export default ModifyOrdersFromExcel;
